import { useTranslation } from "react-i18next";import React, { useState, useEffect, useCallback, useRef } from 'react';
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/shared/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import { devicesAPI } from '@/shared/services/api';
import {
  MapPin,
  Truck,
  Navigation,
  Battery,
  Signal,
  Clock,
  AlertTriangle,
  Settings,
  RefreshCw,
  Maximize2,
  List,
  Map as MapIcon,
  Loader2,
  CheckCircle2,
  XCircle,
  Wifi,
  WifiOff,
  Play,
  Pause,
  Key } from
'lucide-react';
import { toast } from 'sonner';

// Vehicle marker component with custom styling
const VehicleMarker = ({ vehicle, isSelected, onClick }) => {const { t } = useTranslation("wms");
  const statusColors = {
    online: 'bg-green-500',
    offline: 'bg-gray-400',
    moving: 'bg-blue-500',
    idle: 'bg-amber-500',
    alert: 'bg-red-500'
  };

  return (
    <div
      onClick={onClick}
      className={`cursor-pointer transform transition-all duration-200 ${isSelected ? 'scale-125' : 'hover:scale-110'}`}>
      
      <div className={`relative w-10 h-10 ${statusColors[vehicle.status] || 'bg-blue-500'} rounded-full flex items-center justify-center shadow-lg border-2 border-white`}>
        <Truck className="h-5 w-5 text-white" />
        {vehicle.status === 'moving' &&
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
        }
      </div>
      <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
        <span className="text-xs font-semibold bg-white/90 px-1.5 py-0.5 rounded shadow text-gray-800">
          {vehicle.name?.split(' ').pop() || vehicle.id?.slice(-4)}
        </span>
      </div>
    </div>);

};

// API Key Configuration Dialog
const ApiKeyDialog = ({ open, onOpenChange, onSave, currentKey }) => {const { t } = useTranslation("wms");
  const [apiKey, setApiKey] = useState(currentKey || '');

  const handleSave = () => {
    if (apiKey.trim()) {
      onSave(apiKey.trim());
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />{t("configureGoogleMaps", "Configure Google Maps API Key")}

          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t("apiKeyRequired", "API Key Required")}</AlertTitle>
            <AlertDescription className="text-sm">{t("toUseThe", "To use the GPS tracking map, you need a Google Maps API Key.")}

              <a
                href="https://console.cloud.google.com/google/maps-apis/credentials"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline ml-1">{t("getYourApi", "Get your API key here")}


              </a>
            </AlertDescription>
          </Alert>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("googleMapsApi", "Google Maps API Key")}</label>
            <Input
              type="password"
              placeholder="AIzaSy..."
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              data-testid="google-maps-api-key-input" />
            
            <p className="text-xs text-muted-foreground">{t("enableMapsJavascript", "Enable \"Maps JavaScript API\" in your Google Cloud Console")}

            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel", "Cancel")}</Button>
          <Button onClick={handleSave} disabled={!apiKey.trim()}>{t("saveApiKey", "Save API Key")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);

};

// Map container with vehicle tracking
const TrackingMap = ({ vehicles, selectedVehicle, onSelectVehicle, center }) => {const { t } = useTranslation("wms");
  const map = useMap();

  // Center map on selected vehicle
  useEffect(() => {
    if (map && selectedVehicle) {
      map.panTo({
        lat: selectedVehicle.last_coordinates?.lat || selectedVehicle.latitude || -23.5505,
        lng: selectedVehicle.last_coordinates?.lng || selectedVehicle.longitude || -46.6333
      });
    }
  }, [map, selectedVehicle]);

  return (
    <>
      {vehicles.map((vehicle) => {
        const lat = vehicle.last_coordinates?.lat || vehicle.latitude;
        const lng = vehicle.last_coordinates?.lng || vehicle.longitude;

        if (!lat || !lng) return null;

        return (
          <AdvancedMarker
            key={vehicle.id}
            position={{ lat, lng }}
            onClick={() => onSelectVehicle(vehicle)}>
            
            <VehicleMarker
              vehicle={vehicle}
              isSelected={selectedVehicle?.id === vehicle.id}
              onClick={() => onSelectVehicle(vehicle)} />
            
          </AdvancedMarker>);

      })}

      {selectedVehicle &&
      <InfoWindow
        position={{
          lat: selectedVehicle.last_coordinates?.lat || selectedVehicle.latitude || 0,
          lng: selectedVehicle.last_coordinates?.lng || selectedVehicle.longitude || 0
        }}
        onCloseClick={() => onSelectVehicle(null)}>
        
          <div className="p-3 min-w-[220px]">
            <h3 className="font-bold text-lg mb-2">{selectedVehicle.name}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Signal className="h-4 w-4 text-green-500" />
                <span className="capitalize">{selectedVehicle.status}</span>
              </div>
              {selectedVehicle.assigned_vehicle &&
            <div className="flex items-center gap-2">
                  <Truck className="h-4 w-4 text-blue-500" />
                  <span>{selectedVehicle.assigned_vehicle}</span>
                </div>
            }
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-red-500" />
                <span className="font-mono text-xs">
                  {(selectedVehicle.last_coordinates?.lat || 0).toFixed(6)},
                  {(selectedVehicle.last_coordinates?.lng || 0).toFixed(6)}
                </span>
              </div>
              {selectedVehicle.speed !== undefined &&
            <div className="flex items-center gap-2">
                  <Navigation className="h-4 w-4 text-amber-500" />
                  <span>{selectedVehicle.speed || 0}{t("kmh", "km/h")}</span>
                </div>
            }
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{t("updated", "Updated:")}{new Date(selectedVehicle.last_seen).toLocaleTimeString()}</span>
              </div>
            </div>
          </div>
        </InfoWindow>
      }
    </>);

};

// Main GPS Tracking Component
export const GPSTracking = () => {const { t } = useTranslation("wms");
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list'
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [apiKey, setApiKey] = useState(() => {
    // Check localStorage first, then env variable
    return localStorage.getItem('GOOGLE_MAPS_API_KEY') ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    '';
  });
  const [isTracking, setIsTracking] = useState(true);
  const [mapCenter] = useState({ lat: -23.5505, lng: -46.6333 }); // São Paulo default
  const refreshInterval = useRef(null);

  // Fetch GPS devices
  const fetchVehicles = useCallback(async () => {
    try {
      const data = await devicesAPI.getDevices('gps_tracker');
      setVehicles(data || []);
    } catch (error) {
      console.error('Failed to fetch GPS devices:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVehicles();

    // Set up polling for real-time updates when tracking is enabled
    if (isTracking) {
      refreshInterval.current = setInterval(fetchVehicles, 10000); // Update every 10 seconds
    }

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [fetchVehicles, isTracking]);

  const handleSaveApiKey = (key) => {
    setApiKey(key);
    localStorage.setItem('GOOGLE_MAPS_API_KEY', key);
    toast.success('API Key Saved', { description: 'Google Maps will now load with your key' });
  };

  const toggleTracking = () => {
    setIsTracking(!isTracking);
    if (!isTracking) {
      toast.success('Live Tracking Enabled');
    } else {
      toast.info('Live Tracking Paused');
    }
  };

  const onlineCount = vehicles.filter((v) => v.status === 'online').length;

  // Show API key configuration if not set
  if (!apiKey) {
    return (
      <div className="space-y-6" data-testid="gps-tracking">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("gpsTracking", "GPS Tracking")}</h1>
            <p className="text-muted-foreground mt-1">{t("realtimeVehicleAnd", "Real-time vehicle and asset tracking")}</p>
          </div>
        </div>

        <Card className="card-elevated">
          <CardContent className="p-12 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-4">
              <Key className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2">{t("googleMapsApi", "Google Maps API Key Required")}</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">{t("toDisplayThe", "To display the GPS tracking map, you need to configure your Google Maps API Key.\n              This is a one-time setup.")}


            </p>
            <div className="space-y-4">
              <Button onClick={() => setShowApiKeyDialog(true)} className="gap-2">
                <Key className="h-4 w-4" />{t("configureApiKey", "Configure API Key")}

              </Button>
              <p className="text-xs text-muted-foreground">{t("dontHaveAn", "Don't have an API key?")}
                {' '}
                <a
                  href="https://console.cloud.google.com/google/maps-apis/credentials"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline">{t("getOneFrom", "Get one from Google Cloud Console")}


                </a>
              </p>
            </div>
          </CardContent>
        </Card>

        <ApiKeyDialog
          open={showApiKeyDialog}
          onOpenChange={setShowApiKeyDialog}
          onSave={handleSaveApiKey}
          currentKey={apiKey} />
        
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="gps-tracking">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("gpsTracking", "GPS Tracking")}</h1>
          <p className="text-muted-foreground mt-1">{t("realtimeVehicleAnd", "Real-time vehicle and asset tracking")}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={isTracking ? "default" : "outline"}
            onClick={toggleTracking}
            className="gap-2">
            
            {isTracking ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {isTracking ? 'Tracking' : 'Paused'}
          </Button>
          <Button variant="outline" onClick={fetchVehicles} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" onClick={() => setShowApiKeyDialog(true)} className="gap-2">
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("totalTrackers", "Total Trackers")}</p>
              <p className="text-2xl font-bold">{vehicles.length}</p>
            </div>
            <MapPin className="h-8 w-8 text-muted-foreground/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-green-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("online", "Online")}</p>
              <p className="text-2xl font-bold text-green-600">{onlineCount}</p>
            </div>
            <Wifi className="h-8 w-8 text-green-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("moving", "Moving")}</p>
              <p className="text-2xl font-bold text-blue-600">
                {vehicles.filter((v) => v.speed > 0).length}
              </p>
            </div>
            <Navigation className="h-8 w-8 text-blue-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-gray-400">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("offline", "Offline")}</p>
              <p className="text-2xl font-bold text-gray-500">
                {vehicles.length - onlineCount}
              </p>
            </div>
            <WifiOff className="h-8 w-8 text-gray-400/30" />
          </CardContent>
        </Card>
      </div>

      {/* View Toggle */}
      <div className="flex items-center gap-2">
        <Button
          variant={viewMode === 'map' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('map')}
          className="gap-2">
          
          <MapIcon className="h-4 w-4" />{t("mapView", "Map View")}

        </Button>
        <Button
          variant={viewMode === 'list' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('list')}
          className="gap-2">
          
          <List className="h-4 w-4" />{t("listView", "List View")}

        </Button>
        {isTracking &&
        <Badge variant="default" className="bg-green-600 gap-1 ml-auto">
            <span className="h-2 w-2 rounded-full bg-white animate-pulse" />{t("live", "Live")}

        </Badge>
        }
      </div>

      {/* Map View */}
      {viewMode === 'map' &&
      <Card className="card-elevated overflow-hidden">
          <div className="h-[500px] relative">
            <APIProvider
            apiKey={apiKey}
            onLoad={() => console.log('Google Maps API loaded')}>
            
              <Map
              style={{ width: '100%', height: '100%' }}
              defaultCenter={mapCenter}
              defaultZoom={12}
              gestureHandling="greedy"
              disableDefaultUI={false}
              mapTypeControl={true}
              streetViewControl={false}>
              
                <TrackingMap
                vehicles={vehicles}
                selectedVehicle={selectedVehicle}
                onSelectVehicle={setSelectedVehicle}
                center={mapCenter} />
              
              </Map>
            </APIProvider>

            {/* Vehicle List Overlay */}
            <div className="absolute top-4 left-4 z-10 bg-white dark:bg-gray-800 rounded-lg shadow-lg max-h-80 overflow-y-auto w-64">
              <div className="p-3 border-b border-border">
                <h4 className="font-semibold text-sm">{t("trackedVehicles", "Tracked Vehicles")}</h4>
              </div>
              <div className="divide-y divide-border">
                {vehicles.map((vehicle) =>
              <button
                key={vehicle.id}
                onClick={() => setSelectedVehicle(vehicle)}
                className={`w-full p-3 text-left hover:bg-muted/50 transition-colors ${selectedVehicle?.id === vehicle.id ? 'bg-muted' : ''}`
                }>
                
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${vehicle.status === 'online' ? 'bg-green-500' : 'bg-gray-400'}`
                  } />
                      <span className="font-medium text-sm truncate">{vehicle.name}</span>
                    </div>
                    {vehicle.assigned_vehicle &&
                <p className="text-xs text-muted-foreground ml-4">{vehicle.assigned_vehicle}</p>
                }
                  </button>
              )}
                {vehicles.length === 0 &&
              <div className="p-4 text-center text-sm text-muted-foreground">{t("noGpsTrackers", "No GPS trackers found")}

              </div>
              }
              </div>
            </div>
          </div>
        </Card>
      }

      {/* List View */}
      {viewMode === 'list' &&
      <Card className="card-elevated">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-medium">{t("device", "Device")}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">{t("vehicle", "Vehicle")}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">{t("status", "Status")}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">{t("location", "Location")}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">{t("lastUpdate", "Last Update")}</th>
                    <th className="text-right py-3 px-4 text-sm font-medium">{t("actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {vehicles.map((vehicle) =>
                <tr key={vehicle.id} className="hover:bg-muted/30">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-amber-500" />
                          <span className="font-medium">{vehicle.name}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{vehicle.model}</p>
                      </td>
                      <td className="py-3 px-4">
                        {vehicle.assigned_vehicle || '-'}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={vehicle.status === 'online' ? 'default' : 'secondary'}>
                          {vehicle.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 font-mono text-xs">
                        {vehicle.last_coordinates ?
                    `${(vehicle.last_coordinates?.lat ?? 0).toFixed(4)}, ${(vehicle.last_coordinates?.lng ?? 0).toFixed(4)}` :
                    '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {new Date(vehicle.last_seen).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedVehicle(vehicle);
                        setViewMode('map');
                      }}>{t("viewOnMap", "View on Map")}


                    </Button>
                      </td>
                    </tr>
                )}
                </tbody>
              </table>
              {vehicles.length === 0 &&
            <div className="p-8 text-center text-muted-foreground">{t("noGpsTracking", "No GPS tracking devices found. Add GPS trackers in Device Management.")}

            </div>
            }
            </div>
          </CardContent>
        </Card>
      }

      {/* API Key Dialog */}
      <ApiKeyDialog
        open={showApiKeyDialog}
        onOpenChange={setShowApiKeyDialog}
        onSave={handleSaveApiKey}
        currentKey={apiKey} />
      
    </div>);

};

export default GPSTracking;